import { useRef, useState, useCallback, useEffect } from 'react';
import * as poseDetection from '@tensorflow-models/pose-detection';
import { initializeTensorFlow } from '../utils/tfInit';
import { createYoloDetector, createYoloPoseDetector } from '../utils/detectorFactory';
import { createCounter } from '../counters/counterRegistry.js';
import { normalizeFrameForCounter } from '../utils/detectionUtils.js';

/**
 * Detector types supported by the hook
 */
export const DETECTOR_TYPES = {
  POSE: 'pose',      // MoveNet for jump rope
  YOLO: 'yolo'       // YOLO for pickleball
};

/**
 * Counter types that map to detector types
 */
export const COUNTER_TYPES = {
  'skipping': { detector: DETECTOR_TYPES.POSE, counterId: 'skipping' },
  'pickleball': { detector: DETECTOR_TYPES.YOLO, counterId: 'pickleball' },
  'soccer': { detector: DETECTOR_TYPES.YOLO, counterId: 'soccer' }
};

/**
 * Update counter with frame data - SINGLE SOURCE OF TRUTH
 *
 * This is the ONLY function that should update a counter with frame data.
 * Both live counting and replay MUST use this function to ensure consistency.
 *
 * Code paths that use this:
 * - Live counting: BaseExerciseStrategy → useDetector.updateCounter → updateCounterWithFrame
 * - Replay: PhysicalDemo.runReplay → updateCounterWithFrame
 *
 * @param {Object} counter - Counter instance (XGBoostJumpCounter, etc.)
 * @param {Array|Object} frameData - Raw poses array or frameData object with keypoints
 * @param {string} detectorType - DETECTOR_TYPES.POSE or DETECTOR_TYPES.YOLO
 * @param {number} timestamp - Frame timestamp in ms
 * @param {Object} videoDimensions - { width, height } of video
 * @returns {number} Current rep count
 */
/**
 * Update counter with frame data - SINGLE SOURCE OF TRUTH
 *
 * This is the ONLY function that should update a counter with frame data.
 * Both live counting and replay MUST use this function to ensure consistency.
 *
 * For YOLO detectors, this function normalizes the frame data to ensure
 * identical format for both live and replay:
 * - Live: buildFrameData() output → normalizeFrameForCounter() → counter
 * - Replay: recording frame → normalizeFrameForCounter() → counter
 *
 * @param {Object} counter - Counter instance
 * @param {Array|Object} frameData - Raw poses array or frameData object
 * @param {string} detectorType - DETECTOR_TYPES.POSE or DETECTOR_TYPES.YOLO
 * @param {number} timestamp - Frame timestamp in ms
 * @param {Object} videoDimensions - { width, height } of video
 * @returns {number} Current rep count
 */
export function updateCounterWithFrame(counter, frameData, detectorType, timestamp, videoDimensions) {
  if (!counter) return 0;

  if (detectorType === DETECTOR_TYPES.POSE) {
    // Extract keypoints from either raw poses array or frameData object
    let keypoints = null;

    if (frameData?.length > 0) {
      // Raw poses array from detect()
      keypoints = frameData[0].keypoints;
    } else if (frameData?.keypoints) {
      // frameData object from strategy's buildFrameData() or recording
      keypoints = frameData.keypoints;
    }

    if (keypoints) {
      return counter.update(keypoints, timestamp, videoDimensions);
    }
  } else if (detectorType === DETECTOR_TYPES.YOLO) {
    // YOLO: normalize frame to counter format, then pass to counter
    // This is the THIN NORMALIZATION LAYER - single point of format conversion
    const normalizedFrame = normalizeFrameForCounter(frameData || {});
    return counter.update(normalizedFrame, timestamp ?? Date.now(), videoDimensions);
  }

  return counter.repCount || counter.strokeCount || 0;
}

/**
 * Shared detector hook for exercise detection
 *
 * @param {Object} options
 * @param {string} options.exerciseType - 'jumprope' or 'pickleball'
 * @param {string} options.modelPath - Path to model (for YOLO)
 * @param {string} options.railsEnv - Rails environment ('development', 'test', 'production')
 * @returns {Object} Detector state and controls
 */
/**
 * Default YOLO model paths per exercise type
 */
const DEFAULT_MODEL_PATHS = {
  'pickleball': '/pickleball-model/model.json',
  'soccer': '/soccer-model/model.json'
};

/**
 * ONNX model paths per exercise type.
 * Points to model directory; actual filename comes from manifest.detector.json
 * (`onnxModel` field). All three activities have downgraded (opset 19/20)
 * models to work around ORT WebGPU's opset-22 Conv kernel gap.
 */
const ONNX_MODEL_PATHS = {
  'pickleball': '/pickleball-model/model.onnx',
  'soccer': '/soccer-model/model.onnx',
  'skipping': '/pose-model/model.onnx'  // YOLO11n-pose (MoveNet-compatible output)
};

export function useDetector(options = {}) {
  const {
    exerciseType = 'skipping',
    modelPath,
    railsEnv = 'development',
    engine: initialEngine = 'tfjs-webgl',  // Start with WebGL for stability
    disableXGBoost = false  // Disable XGBoost prediction (for performance debugging)
  } = options;

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [currentEngine, setCurrentEngine] = useState(initialEngine);
  const [isSwitchingEngine, setIsSwitchingEngine] = useState(false);

  const detectorRef = useRef(null);
  const counterRef = useRef(null);
  const currentEngineRef = useRef(initialEngine);

  // Compute effective model path based on current engine
  const getEffectiveModelPath = useCallback((engineId) => {
    if (engineId?.startsWith('onnx') && ONNX_MODEL_PATHS[exerciseType]) {
      return ONNX_MODEL_PATHS[exerciseType];
    }
    return modelPath || DEFAULT_MODEL_PATHS[exerciseType] || '/pickleball-model/model.json';
  }, [exerciseType, modelPath]);

  // Get detector/counter config based on exercise type
  const config = COUNTER_TYPES[exerciseType] || COUNTER_TYPES.skipping;

  // Initialize detector with specific engine
  const initializeDetector = useCallback(async (engineId) => {
    const effectiveModelPath = getEffectiveModelPath(engineId);

    if (config.detector === DETECTOR_TYPES.POSE) {
      // ONNX engine → YOLO11n-pose (MoveNet-compatible output shape)
      if (engineId?.startsWith('onnx')) {
        const detector = createYoloPoseDetector(effectiveModelPath, {
          railsEnv,
          engine: engineId,
          confThreshold: 0.25
        });
        await detector.initialize();
        return detector;
      }

      // Otherwise: MoveNet Lightning on TF.js (fastest, 192x192)
      await initializeTensorFlow();

      const detectorConfig = {
        modelType: poseDetection.movenet.modelType.SINGLEPOSE_LIGHTNING,
        enableSmoothing: true,
        minPoseScore: 0.25
      };

      return await poseDetection.createDetector(
        poseDetection.SupportedModels.MoveNet,
        detectorConfig
      );
    } else if (config.detector === DETECTOR_TYPES.YOLO) {
      // Initialize YOLO detector (uses mock in test mode via factory)
      const detector = createYoloDetector(effectiveModelPath, {
        railsEnv,
        engine: engineId,
        confThreshold: 0.25
      });

      await detector.initialize();
      return detector;
    }

    return null;
  }, [config.detector, getEffectiveModelPath, railsEnv]);

  // Initialize detector and counter
  const initialize = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      detectorRef.current = await initializeDetector(currentEngineRef.current);

      // Sync currentEngine state with actual backend used by detector
      const modelInfo = detectorRef.current?.getModelInfo?.();
      if (modelInfo?.backend) {
        const actualEngine = `tfjs-${modelInfo.backend}`;
        if (actualEngine !== currentEngineRef.current) {
          console.log(`[useDetector] Actual backend: ${modelInfo.backend}, updating state`);
          currentEngineRef.current = actualEngine;
          setCurrentEngine(actualEngine);
        }
      }

      // Initialize counter only if not already initialized (preserve state on engine switch)
      if (!counterRef.current) {
        counterRef.current = createCounter(config.counterId);

        // Set disableXGBoost flag for performance debugging
        if (counterRef.current) {
          counterRef.current.disablePrediction = disableXGBoost;
        }

        // Initialize XGBoost model if needed (async)
        if (counterRef.current.initialize) {
          const initResult = await counterRef.current.initialize();
          if (initResult && !initResult.success) {
            setError(initResult.error || 'Failed to initialize ML model');
            setIsLoading(false);
            return false;
          }
        }
      }

      setIsReady(true);
      setIsLoading(false);
      return true;
    } catch (err) {
      console.error('Failed to initialize detector:', err);
      setError(`Failed to load model: ${err.message}`);
      setIsLoading(false);
      return false;
    }
  }, [config.counterId, initializeDetector, disableXGBoost]);

  // Switch engine mid-inference (preserves counter/ByteTrack state)
  const switchEngine = useCallback(async (newEngine) => {
    if (newEngine === currentEngineRef.current) return true;
    if (isSwitchingEngine) return false;

    console.log(`[useDetector] Switching engine: ${currentEngineRef.current} -> ${newEngine}`);
    setIsSwitchingEngine(true);

    try {
      // Stop old detector
      if (detectorRef.current) {
        if (typeof detectorRef.current.stop === 'function') {
          await detectorRef.current.stop();
        } else if (typeof detectorRef.current.dispose === 'function') {
          detectorRef.current.dispose();
        }
        detectorRef.current = null;
      }

      // Update engine state
      currentEngineRef.current = newEngine;
      setCurrentEngine(newEngine);

      // Create new detector with new engine
      detectorRef.current = await initializeDetector(newEngine);

      console.log(`[useDetector] Engine switched to ${newEngine}`);
      setIsSwitchingEngine(false);
      return true;
    } catch (err) {
      console.error('Failed to switch engine:', err);
      setError(`Failed to switch engine: ${err.message}`);
      setIsSwitchingEngine(false);
      return false;
    }
  }, [initializeDetector, isSwitchingEngine]);

  // Run detection
  const detect = useCallback(async (videoElement) => {
    if (!detectorRef.current || !videoElement) return null;

    if (config.detector === DETECTOR_TYPES.POSE) {
      // MoveNet returns poses array
      return await detectorRef.current.estimatePoses(videoElement);
    } else if (config.detector === DETECTOR_TYPES.YOLO) {
      // YOLO returns detections array
      return await detectorRef.current.detect(videoElement);
    }

    return null;
  }, [config.detector]);

  // Update counter with detection results
  // Uses shared updateCounterWithFrame to ensure consistency with replay
  const updateCounter = useCallback((detectionResults, videoDimensions = null, timestamp = null) => {
    return updateCounterWithFrame(counterRef.current, detectionResults, config.detector, timestamp, videoDimensions);
  }, [config.detector]);

  // Get counter state
  const getCounterState = useCallback(() => {
    if (!counterRef.current) return null;
    return counterRef.current.getState?.() || {
      repCount: counterRef.current.repCount || counterRef.current.strokeCount || 0
    };
  }, []);

  // Get model version info (YOLO detector + XGBoost counter)
  const getModelVersions = useCallback(() => {
    return {
      yolo: detectorRef.current?.getModelInfo?.() || null,
      xgboost: counterRef.current?.modelInfo || null
    };
  }, []);

  // Reset counter
  const resetCounter = useCallback(() => {
    if (counterRef.current?.reset) {
      counterRef.current.reset();
    }
  }, []);

  // Get rep count
  const getRepCount = useCallback(() => {
    if (!counterRef.current) return 0;
    return counterRef.current.repCount || counterRef.current.strokeCount || 0;
  }, []);

  // Cleanup
  const cleanup = useCallback(() => {
    if (detectorRef.current) {
      if (typeof detectorRef.current.dispose === 'function') {
        detectorRef.current.dispose();
      } else if (typeof detectorRef.current.stop === 'function') {
        detectorRef.current.stop();
      }
      detectorRef.current = null;
    }
    counterRef.current = null;
    setIsReady(false);
  }, []);

  // Auto-initialize on mount
  useEffect(() => {
    let mounted = true;

    const doInit = async () => {
      await initialize();
    };

    doInit();

    return () => {
      mounted = false;
      cleanup();
    };
  }, [exerciseType]); // Re-initialize when exercise type changes

  return {
    // State
    isLoading,
    isReady,
    error,
    currentEngine,
    isSwitchingEngine,

    // Detector info
    detectorType: config.detector,
    exerciseType,

    // Controls
    initialize,
    detect,
    cleanup,
    switchEngine,

    // Counter controls
    updateCounter,
    getCounterState,
    resetCounter,
    getRepCount,
    getModelVersions,

    // Refs for advanced usage
    detectorRef,
    counterRef
  };
}
